import { ReactElement, useMemo, useState } from 'react';
import cx from 'clsx';
import { startOfYear, formatISO } from 'date-fns';
import { Breadcrumb, DownloadButton, EnterpriseField, Heading, Page, PeriodPickerField, withHasPerm } from 'components';
import { DateRangePickerField, Form } from '@ff-it/form';
import { FormSpy } from 'react-final-form';
import useSWR from 'swr';
import permissions from '../permissions';
import { any, useHasPerms } from 'core/permissions';
import { usePeriod } from 'modules/reporting/usePeriod';
import { CompanyField } from 'modules/core/company/components';
import { StateFilterField, TypeFilterField } from 'modules/reporting/Filter';
import { RawExportSchema } from '../columns';
import { ExportColumnsField } from './ExportColumnsField';

const onSubmit = (): void => undefined;

type ReportType = 'raw' | 'qpack';

function RenderReport({
  global,
  managed,
  canRaw = true,
  canQpack = false,
}: {
  global: boolean;
  managed?: boolean;
  canRaw?: boolean;
  canQpack?: boolean;
}): ReactElement {
  const [selectedReport, setReportType] = useState<ReportType>('raw');
  let reportType = selectedReport;
  if (!canRaw) {
    reportType = 'qpack';
  } else if (!canQpack) {
    reportType = 'raw';
  }
  const rawUrl = `reporting/raw/${global ? 'global' : managed ? 'local_managed' : 'local'}/`;
  const { data: columnSchema, error: columnError } = useSWR<RawExportSchema>(
    reportType === 'raw' ? { method: 'GET', url: rawUrl } : null,
  );
  const initialValues = useMemo(
    () => ({
      date_from: formatISO(startOfYear(new Date()), { representation: 'date' }),
      date_to: null,
      agency: [],
      client: [],
      payer: [],
      provider: [],
    }),
    [],
  );

  const { date_from: minDate, date_to: maxDate } = usePeriod();
  return (
    <Page>
      <Breadcrumb to={null}>Raw report</Breadcrumb>
      <Heading title="Raw report"></Heading>
      <ul className="nav nav-tabs mr-auto mb-3">
        {canRaw && (
          <li className="nav-item">
            <button
              type="button"
              className={cx('nav-link', reportType === 'raw' && 'active')}
              aria-pressed={reportType === 'raw'}
              onClick={() => setReportType('raw')}
            >
              Raw
            </button>
          </li>
        )}
        {canQpack && (
          <li className="nav-item">
            <button
              type="button"
              className={cx('nav-link', reportType === 'qpack' && 'active')}
              aria-pressed={reportType === 'qpack'}
              onClick={() => setReportType('qpack')}
            >
              Qpack
            </button>
          </li>
        )}
      </ul>
      <div className="container px-0 ml-0">
        <Form onSubmit={onSubmit} initialValues={initialValues} autoComplete="off">
          <div className="form-row">
            {global && (
              <EnterpriseField
                name="agency"
                isMulti
                className="col"
                defaultOptions
                placeholder="All agencies"
                label="Agencies"
              />
            )}
            <div className="col-3 mb-3">
              <DateRangePickerField
                label="Period"
                fromName="date_from"
                toName="date_to"
                minDate={minDate}
                maxDate={maxDate}
                className="mb-1"
                showMonthYearPicker
                dateFormat="MMM. yyyy"
                fromPlaceholder="MMM. yyyy"
                toPlaceholder="MMM. yyyy"
              />
              <PeriodPickerField fromName="date_from" toName="date_to" minDate={minDate} maxDate={maxDate} />
            </div>
            <StateFilterField label="State" className="col" />
            <TypeFilterField global={global} className="col" label="Type" />
          </div>
          <div className="form-row">
            <CompanyField name="client" label="Client" className="col" isMulti includeBranches={true} />
            <CompanyField name="payer" label="Payer" className="col" isMulti includeBranches={true} />
            <CompanyField name="provider" label="Provider" className="col" isMulti includeBranches={true} />
          </div>
          {reportType === 'raw' &&
            (columnSchema ? (
              <ExportColumnsField
                key={columnSchema.export_key}
                groups={columnSchema.groups}
                exportKey={columnSchema.export_key}
              />
            ) : (
              <p role={columnError ? 'alert' : 'status'}>
                {columnError ? 'Unable to load report columns.' : 'Loading columns…'}
              </p>
            ))}

          <FormSpy subscription={{ values: true, invalid: true }}>
            {({ values: { columns, ...filters }, invalid }) =>
              (reportType === 'qpack' || columnSchema) && (
                <DownloadButton
                  method="POST"
                  body={reportType === 'raw' ? { ...filters, columns } : filters}
                  url={reportType === 'qpack' ? 'reporting/qpack/local/' : rawUrl}
                  icon="file-excel"
                  title="Export"
                  disabled={reportType === 'raw' && (invalid || !columns)}
                >
                  Export
                </DownloadButton>
              )
            }
          </FormSpy>
        </Form>
      </div>
    </Page>
  );
}

export const Local = withHasPerm(
  () => {
    const [local, managed, qpack] = useHasPerms(permissions.local, permissions.local_managed, permissions.qpack);
    return <RenderReport global={false} managed={!local} canRaw={local || managed} canQpack={qpack} />;
  },
  any(permissions.local, permissions.local_managed, permissions.qpack),
);
export const Global = withHasPerm(() => <RenderReport global={true} />, permissions.global);
